/**
 * Apartment metrix filter utility functions.
 * All functions are pure — no side effects, easy to test.
 */

/** Parse a string/number value to a number, removing commas. Returns NaN if invalid. */
export function parseNumeric(value) {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number') return value;
  return parseFloat(String(value).replace(/,/g, ''));
}

/** Check if value is within [min, max] range. null min/max means unbounded. */
export function isInRange(value, min, max) {
  if (isNaN(value)) return false;
  if (min !== null && value < min) return false;
  if (max !== null && value > max) return false;
  return true;
}

export function filterByPrice(item, filter) {
  if (filter.minPrice === null && filter.maxPrice === null) return true;
  if (item.sale_price === null) return false;
  const value = parseNumeric(item.sale_price);
  return isInRange(value, filter.minPrice, filter.maxPrice);
}

export function filterByHousehold(item, filter) {
  if (filter.minHousehold === null && filter.maxHousehold === null) return true;
  if (item.total_households === null || item.total_households === undefined) return false;
  const value = parseNumeric(item.total_households);
  return isInRange(value, filter.minHousehold, filter.maxHousehold);
}

export function filterByArea(item, filter) {
  if (filter.minArea === null && filter.maxArea === null) return true;
  if (item.exclusive_area === null || item.exclusive_area === undefined) return false;
  const value = parseNumeric(item.exclusive_area);
  return isInRange(value, filter.minArea, filter.maxArea);
}

export function filterByGap(item, filter) {
  if (filter.minGap === null && filter.maxGap === null) return true;
  if (item.trading_gap === null || item.trading_gap === undefined) return false;
  const value = parseNumeric(item.trading_gap);
  return isInRange(value, filter.minGap, filter.maxGap);
}

export function filterByMoveInDate(item, filter, currentYear) {
  if (filter.minYears === null && filter.maxYears === null) return true;
  if (!item.move_in_date) return false;
  const moveInYear = parseInt(item.move_in_date.substring(0, 4), 10);
  if (isNaN(moveInYear)) return false;
  const yearsOld = currentYear - moveInYear;
  return isInRange(yearsOld, filter.minYears, filter.maxYears);
}

export function filterBySubdistrict(item, filter) {
  if (filter === null) return true;
  if (filter.length === 0) return false;
  if (!item.subdistrict_name) return false;
  return filter.includes(item.subdistrict_name);
}

/** Parse pipe/comma separated walking times and return the minimum. */
function getMinWalkingTime(walkingTimeStr) {
  if (!walkingTimeStr) return Infinity;
  let min = Infinity;
  walkingTimeStr.split(',').forEach(group => {
    group.split('|').forEach(t => {
      const n = parseInt(t.trim(), 10);
      if (!isNaN(n) && n < min) min = n;
    });
  });
  return min;
}

/** Extract all subway line names from comma/pipe separated string. */
function extractAllLines(lineNameStr) {
  if (!lineNameStr) return [];
  const lines = [];
  lineNameStr.split(',').forEach(group => {
    group.split('|').forEach(l => {
      const name = l.trim();
      if (name && !lines.includes(name)) lines.push(name);
    });
  });
  return lines;
}

export function filterBySubway(item, filter) {
  if (filter === null) return true;

  // Object filter: { lines, maxWalkingTime }
  if (typeof filter === 'object' && !Array.isArray(filter)) {
    const { lines, maxWalkingTime } = filter;
    if (!item.subwaystationname || item.subwaystationname.trim() === '') return false;

    if (maxWalkingTime !== null) {
      if (getMinWalkingTime(item.subwaywalkingtime) > maxWalkingTime) return false;
    }

    if (lines && lines.length > 0) {
      const allLines = extractAllLines(item.subwaylinename);
      if (!allLines.some(line => lines.includes(line))) return false;
    }

    return true;
  }

  // Array filter: line names
  if (Array.isArray(filter) && filter.length > 0) {
    if (!item.subwaystationname || !item.subwaylinename) return false;
    const allLines = extractAllLines(item.subwaylinename);
    return allLines.some(line => filter.includes(line));
  }

  // Boolean filter
  if (typeof filter === 'boolean') {
    const hasSubway = !!(item.subwaystationname && item.subwaystationname.trim());
    return filter ? hasSubway : !hasSubway;
  }

  return true;
}

export function filterByGangnamTime(item, filter) {
  if (!filter || filter.maxTime === null) return true;
  if (!item.subway_total_time) return false;
  const times = String(item.subway_total_time).split(',');
  return times.some(t => {
    const n = parseInt(t.trim(), 10);
    return !isNaN(n) && n <= filter.maxTime;
  });
}

export function filterBySchool(item, filter) {
  if (filter === null || filter.hasSchool === undefined) return true;
  const hasSchool = !!(item.schoolname && item.schoolname.trim());
  return filter.hasSchool ? hasSchool : !hasSchool;
}

/** Check if any filter is active. */
export function areFiltersActive(filters, aptMetrixInfo) {
  const {
    priceFilter, householdFilter, areaFilter, gapFilter,
    moveInDateFilter, subdistrictFilter, subwayFilter,
    gangnamTimeFilter, schoolFilter,
  } = filters;

  if (priceFilter.minPrice !== null || priceFilter.maxPrice !== null) return true;
  if (householdFilter.minHousehold !== null || householdFilter.maxHousehold !== null) return true;
  if (areaFilter.minArea !== null || areaFilter.maxArea !== null) return true;
  if (gapFilter.minGap !== null || gapFilter.maxGap !== null) return true;
  if (moveInDateFilter.minYears !== null || moveInDateFilter.maxYears !== null) return true;
  if (subdistrictFilter && subdistrictFilter.length > 0) {
    const totalDongs = aptMetrixInfo
      ? [...new Set(aptMetrixInfo.map(i => i.subdistrict_name))].filter(Boolean).length
      : 0;
    if (subdistrictFilter.length < totalDongs) return true;
  }
  if (subwayFilter !== null) return true;
  if (gangnamTimeFilter && gangnamTimeFilter.maxTime !== null) return true;
  if (schoolFilter !== null) return true;
  return false;
}

/** Apply all filters to data array. Returns filtered array or null. */
export function applyAllFilters(data, filters) {
  if (!data) return null;

  const {
    priceFilter, householdFilter, areaFilter, gapFilter,
    moveInDateFilter, subdistrictFilter, subwayFilter,
    gangnamTimeFilter, schoolFilter,
  } = filters;

  // Check if any filter is set
  const hasAnyFilter =
    priceFilter.minPrice !== null || priceFilter.maxPrice !== null ||
    householdFilter.minHousehold !== null || householdFilter.maxHousehold !== null ||
    areaFilter.minArea !== null || areaFilter.maxArea !== null ||
    gapFilter.minGap !== null || gapFilter.maxGap !== null ||
    moveInDateFilter.minYears !== null || moveInDateFilter.maxYears !== null ||
    subdistrictFilter !== null ||
    subwayFilter !== null ||
    (gangnamTimeFilter && gangnamTimeFilter.maxTime !== null) ||
    schoolFilter !== null;

  if (!hasAnyFilter) return data;

  const currentYear = new Date().getFullYear();

  return data.filter(item =>
    filterByPrice(item, priceFilter) &&
    filterByHousehold(item, householdFilter) &&
    filterByArea(item, areaFilter) &&
    filterByGap(item, gapFilter) &&
    filterByMoveInDate(item, moveInDateFilter, currentYear) &&
    filterBySubdistrict(item, subdistrictFilter) &&
    filterBySubway(item, subwayFilter) &&
    filterByGangnamTime(item, gangnamTimeFilter) &&
    filterBySchool(item, schoolFilter)
  );
}
